'use client';

import { useCallback, useEffect, useRef } from 'react';

import { ALGOLIA_SEARCH_QUERY_DEBOUNCE_MS } from 'commons/constants';

const registeredResets = new Set();

// Full reset (timer + typed query). Used when the modal closes / search clears.
export const cancelDebouncedAlgoliaQuery = () => {
  registeredResets.forEach(resetFn => resetFn());
};

const useDebouncedAlgoliaQueryHook = (
  delayMs = ALGOLIA_SEARCH_QUERY_DEBOUNCE_MS,
  { registerReset = true } = {}
) => {
  const timerRef = useRef(null);
  const typedQueryRef = useRef('');

  const cancel = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    cancel();
    typedQueryRef.current = '';
  }, [cancel]);

  useEffect(() => {
    if (!registerReset) return cancel;

    registeredResets.add(reset);
    return () => {
      reset();
      registeredResets.delete(reset);
    };
  }, [cancel, registerReset, reset]);

  const queryHook = useCallback(
    (query, search) => {
      typedQueryRef.current = query;

      if (timerRef.current != null) {
        clearTimeout(timerRef.current);
      }

      if (!query) {
        timerRef.current = null;
        search(query);
        return;
      }

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        search(query);
      }, delayMs);
    },
    [delayMs]
  );

  const getTypedQuery = useCallback(() => typedQueryRef.current, []);

  return { queryHook, cancel, reset, getTypedQuery };
};

export default useDebouncedAlgoliaQueryHook;
